import { Input, Label, TextArea, TextField } from '@heroui/react';

// ⚠ **ΕΙΣΑΓΩΓΗ, ΟΧΙ ΑΝΤΙΓΡΑΦΗ.** Ο κανόνας ανάγνωσης του ποσού γράφτηκε για την
// επέκταση και ζει εκεί, σε άρθρωμα χωρίς εξαρτήσεις από περιηγητή. Δεύτερη
// διατύπωσή του εδώ θα σήμαινε ότι η **ίδια** πρόσκληση αποθηκεύεται με
// διαφορετικό ποσό ανάλογα με το πού καταχωρήθηκε — ακριβώς αυτό που η **D36**
// απαγορεύει ρητά. Το άρθρωμα είναι ήδη προσβάσιμο από εδώ: η σουίτα του `web/`
// το εισάγει από την ίδια διαδρομή, οπότε δεν χρειάστηκε μετακόμιση.
import { parseAmountInput } from '../../../../extension/src/lib/funding.js';

import { FUNDING } from '../../i18n/el';

export const EMPTY_FUNDING = Object.freeze({
    agency: '',
    field: '',
    deadline: '',
    amount: '',
    eligibleEntities: '',
    terms: ''
});

/**
 * Τα στοιχεία χρηματοδότησης ενός πόρου ως πρόχειρο φόρμας.
 *
 * **Η ανάγνωση δεν εμπιστεύεται τη μορφή.** Το πεδίο φτάνει εδώ σε τρεις μορφές, και
 * οι τρεις νόμιμες: `null` για κάθε πόρο που δεν έχει στοιχεία χρηματοδότησης — έτσι
 * το δίνει το API, που μεταφράζει κάθε απόν πεδίο σε ρητό `null` ώστε το σχήμα της
 * απόκρισης να μην εξαρτάται από το περιεχόμενο· `undefined` όταν ο πόρος χτίζεται
 * τοπικά· και κενό αντικείμενο από τη φόρμα. Καμία από τις τρεις δεν επιτρέπεται να
 * ρίξει την οθόνη.
 *
 * Προεπιλογή παραμέτρου **δεν αρκεί**: πιάνει μόνο το `undefined`. Η διάκριση δεν
 * είναι λεπτομέρεια της γλώσσας — είναι η διαφορά ανάμεσα σε «δεν αποθηκεύτηκε»,
 * που ισχύει στη βάση, και «δεν υπάρχει», που είναι αυτό που λέει το σύρμα.
 *
 * Ο κατάλογος των πεδίων βγαίνει από το `EMPTY_FUNDING` και όχι από το `funding`:
 * έτσι το πρόχειρο έχει πάντα και τα έξι κλειδιά, ανεξάρτητα από το τι έστειλε ο
 * εξυπηρετητής, και τα άγνωστα κλειδιά δεν περνούν στο `.strict()` του API.
 */
export function fundingDraftOf(funding) {
    const source = funding ?? {};
    const draft = {};

    for (const key of Object.keys(EMPTY_FUNDING)) {
        draft[key] = source[key] ?? '';
    }
    draft.deadline = source.deadline ? String(source.deadline).slice(0, 10) : '';

    return draft;
}

/**
 * Το πρόχειρο έτοιμο για αποστολή, με το **ποσό διαβασμένο**.
 *
 * Επιστρέφει `{ funding }` ή `{ error }` — ποτέ και τα δύο, και ποτέ σιωπηλά
 * κενό ποσό. Κατά την **D34**, ό,τι δεν διαβάζεται **σταματά** την αποθήκευση με
 * μήνυμα που λέει τι να κάνει ο χρήστης· η εναλλακτική —να σταλεί κενό— είναι
 * ακριβώς το σφάλμα που το πεδίο τύπου αριθμού έκανε από μόνο του.
 *
 * Το κενό ποσό **δεν** είναι σφάλμα και **δεν** είναι μηδέν: σημαίνει «δεν
 * καταγράφηκε ύψος χρηματοδότησης».
 */
export function fundingPayloadOf(draft) {
    const source = draft ?? EMPTY_FUNDING;
    const amount = parseAmountInput(source.amount);

    if (amount === null) return { error: FUNDING.amountUnreadable };
    return { funding: { ...source, amount } };
}

/** Τα έξι προαιρετικά πεδία της ΛΑ-29, κοινά σε καταχώρηση και επεξεργασία. */
export default function FundingFields({ value, onChange }) {
    // Ίδιος λόγος με το `fundingDraftOf`: το `null` περνά από την προεπιλογή παραμέτρου.
    const funding = value ?? EMPTY_FUNDING;
    const set = (name) => (nextValue) => onChange({ ...funding, [name]: nextValue });

    return (
        <fieldset className="flex flex-col gap-4 rounded-xl border border-border p-4">
            <legend className="px-2 font-semibold">{FUNDING.section}</legend>
            <p className="text-sm text-muted">{FUNDING.optionalHelp}</p>

            <div className="grid gap-4 sm:grid-cols-2">
                <TextField value={funding.agency ?? ''} onChange={set('agency')}>
                    <Label>{FUNDING.agency}</Label>
                    <Input placeholder={FUNDING.agencyPlaceholder} />
                </TextField>
                <TextField value={funding.field ?? ''} onChange={set('field')}>
                    <Label>{FUNDING.field}</Label>
                    <Input placeholder={FUNDING.fieldPlaceholder} />
                </TextField>
                <TextField value={funding.deadline ?? ''} onChange={set('deadline')}>
                    <Label>{FUNDING.deadline}</Label>
                    <Input type="date" />
                </TextField>
                {/* ⚠ **ΚΕΙΜΕΝΟ, ΟΧΙ ΑΡΙΘΜΟΣ — αναγκαστικό, όχι προτίμηση (D36).**
                    Σε πεδίο τύπου αριθμού ο περιηγητής επιβάλλει αγγλοσαξονική μορφή
                    **ανεξάρτητα από τη γλώσσα της διεπαφής**: το ελληνικό
                    «50.000,50» απορρίπτεται **σιωπηλά** —κενή τιμή, κανένα σφάλμα—
                    και το ποσό εξαφανιζόταν με μήνυμα επιτυχίας. Η καταστροφή
                    συμβαίνει **πριν** το δει ο κώδικάς μας, οπότε καμία ανάγνωση δεν
                    μπορεί να τη σώσει. */}
                <TextField value={String(funding.amount ?? '')} onChange={set('amount')}>
                    <Label>{FUNDING.amount}</Label>
                    <Input inputMode="decimal" placeholder={FUNDING.amountPlaceholder} />
                </TextField>
            </div>

            <TextField value={funding.eligibleEntities ?? ''} onChange={set('eligibleEntities')}>
                <Label>{FUNDING.eligibleEntities}</Label>
                <TextArea rows={2} />
            </TextField>
            <TextField value={funding.terms ?? ''} onChange={set('terms')}>
                <Label>{FUNDING.terms}</Label>
                <TextArea rows={3} placeholder={FUNDING.rollingDeadlineHelp} />
            </TextField>
        </fieldset>
    );
}
